"use strict";
var SendGridSignatureGuard_1;
Object.defineProperty(exports, "__esModule", { value: true });
exports.SendGridSignatureGuard = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const crypto = tslib_1.__importStar(require("crypto"));
/**
 * Validates SendGrid Event Webhook signatures using Ed25519 (ECDSA).
 *
 * SendGrid signs the raw POST body + timestamp using an Ed25519 keypair.
 * The public key is available in the SendGrid dashboard:
 *   Settings → Mail Settings → Event Webhook → Signing Key
 *
 * Required env vars:
 *   SENDGRID_WEBHOOK_PUBLIC_KEY  — Base64-encoded DER SPKI public key from SendGrid
 *
 * Relevant headers:
 *   X-Twilio-Email-Event-Webhook-Signature  — Base64 Ed25519 signature
 *   X-Twilio-Email-Event-Webhook-Timestamp  — Unix timestamp string
 *
 * Verification string = rawBody + timestamp   (no separator)
 *
 * If SENDGRID_WEBHOOK_PUBLIC_KEY is not set the guard passes through — stub mode.
 *
 * Docs: https://docs.sendgrid.com/for-developers/tracking-events/getting-started-event-webhook-security-features
 */
let SendGridSignatureGuard = SendGridSignatureGuard_1 = class SendGridSignatureGuard {
    constructor() {
        this.logger = new common_1.Logger(SendGridSignatureGuard_1.name);
    }
    canActivate(context) {
        const publicKeyB64 = process.env['SENDGRID_WEBHOOK_PUBLIC_KEY'];
        if (!publicKeyB64) {
            this.logger.warn('SENDGRID_WEBHOOK_PUBLIC_KEY not set — skipping signature verification (stub mode)');
            return true;
        }
        const req = context.switchToHttp().getRequest();
        const signature = req.headers['x-twilio-email-event-webhook-signature'];
        const timestamp = req.headers['x-twilio-email-event-webhook-timestamp'];
        if (!signature || !timestamp) {
            this.logger.error('Missing SendGrid webhook signature or timestamp headers');
            throw new common_1.UnauthorizedException('Missing SendGrid webhook signature headers');
        }
        const rawBody = req.rawBody;
        if (!rawBody || rawBody.length === 0) {
            this.logger.warn('Raw body unavailable — ensure rawBody: true in NestFactory.create()');
            return true; // Degrade gracefully; tighten in production
        }
        try {
            // Construct Ed25519 public key from DER SPKI buffer
            const keyBuffer = Buffer.from(publicKeyB64, 'base64');
            const keyObject = crypto.createPublicKey({ key: keyBuffer, format: 'der', type: 'spki' });
            // Verification payload = rawBody + timestamp (no separator, per SendGrid spec)
            const verificationPayload = Buffer.concat([rawBody, Buffer.from(timestamp, 'utf8')]);
            const sigBuffer = Buffer.from(signature, 'base64');
            const isValid = crypto.verify(null, // Ed25519 does not use a separate digest algorithm
            verificationPayload, keyObject, sigBuffer);
            if (!isValid) {
                throw new Error('signature mismatch');
            }
        }
        catch (err) {
            this.logger.error(`Invalid SendGrid signature from ${req.ip}: ${err.message}`);
            throw new common_1.UnauthorizedException('Invalid SendGrid event webhook signature');
        }
        return true;
    }
};
exports.SendGridSignatureGuard = SendGridSignatureGuard;
exports.SendGridSignatureGuard = SendGridSignatureGuard = SendGridSignatureGuard_1 = tslib_1.__decorate([
    (0, common_1.Injectable)()
], SendGridSignatureGuard);
